var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { loadImage, releaseCanvases } from "../common";
import { isWebPSupported } from "../common/webp";
import { buildRenderingRequestFromInstructionsUrl } from "@rendering/product-preview";
const buildTransientScene = _ref => {
  let {
    fullBleed,
    dimensions,
    page,
    channel,
    trim = {
      // If no trim is specified, update the trim to the full bleed
      topLeftX: 0,
      topLeftY: 0,
      bottomRightX: fullBleed.width,
      bottomRightY: fullBleed.height
    }
  } = _ref;
  const TRANSIENT_V3_URL = "https://cdn.scenes.documents.cimpress.io/v3/transient";
  // Build out the input data object for transient V3
  const inputData = {
    width: dimensions.width,
    height: dimensions.height,
    page,
    channel,
    product: {
      bleed: fullBleed,
      trim
    }
  };
  return "".concat(TRANSIENT_V3_URL, "?data=").concat(encodeURIComponent(JSON.stringify(inputData)));
};
export const createInstructionsAdapter = _ref2 => {
  let {
    tenant,
    category
  } = _ref2;
  let documentCanvases = {};
  return {
    generateDocumentCanvases(_a) {
      return __awaiter(this, arguments, void 0, function (_ref3) {
        var _this = this;
        let {
          surfaces,
          renderingInstructionsUrl,
          renderingInstructionsUrlOverrides = []
        } = _ref3;
        return function* () {
          const webPSupported = yield isWebPSupported();
          const promises = surfaces.map(surface => {
            var _a, _b;
            return {
              renderingRequest: buildRenderingRequestFromInstructionsUrl({
                instructionsUrl: (_b = (_a = renderingInstructionsUrlOverrides.find(_ref4 => {
                  let {
                    page
                  } = _ref4;
                  return surface.page === page;
                })) === null || _a === void 0 ? void 0 : _a.renderingInstructionsUrl) !== null && _b !== void 0 ? _b : renderingInstructionsUrl,
                referer: category,
                tenantId: tenant,
                view: {
                  type: "scene",
                  name: "",
                  // dummy data
                  purpose: "",
                  // dummy data
                  subPurpose: "",
                  // dummy data
                  scene: {
                    type: "dynamic",
                    url: buildTransientScene({
                      dimensions: surface.textureSize,
                      fullBleed: surface.fullBleed,
                      page: surface.page,
                      channel: surface.channel,
                      trim: surface.trim
                    }),
                    dimensions: {
                      width: surface.textureSize.width,
                      // dummy data
                      height: surface.textureSize.width // dummy data
                    },
                    documents: [{
                      blendingMode: "normal",
                      // dummy data
                      page: surface.page // dummy data
                    }]
                  }
                },
                width: surface.textureSize.width,
                format: webPSupported ? "webp" : surface.preserveAlpha ? "png" : "jpg",
                bgColor: !surface.preserveAlpha ? "FFFFFF" : undefined
              }),
              surface
            };
          }).map(_b => __awaiter(_this, [_b], void 0, function (_ref5) {
            let {
              renderingRequest,
              surface
            } = _ref5;
            return function* () {
              var _c;
              let documentImage;
              if (renderingRequest.method === "GET") {
                documentImage = yield loadImage(renderingRequest.url);
              } else {
                const response = yield fetch(renderingRequest);
                if (!response.ok) {
                  throw new Error("Could not fetch large document image with POST to: ".concat(renderingRequest.url));
                }
                const blob = yield response.clone().blob();
                const url = URL.createObjectURL(blob);
                documentImage = yield loadImage(url);
                URL.revokeObjectURL(url);
              }
              const key = "".concat(surface.page, " ").concat((_c = surface.channel) !== null && _c !== void 0 ? _c : "none");
              if (!documentCanvases[key]) {
                documentCanvases[key] = document.createElement("canvas");
              }
              const canvas = documentCanvases[key];
              const canvasContext = canvas.getContext("2d");
              // Get the texture size
              const {
                width,
                height
              } = surface.textureSize;
              // Update the canvas to the current texture size
              canvas.width = width;
              canvas.height = height;
              // Clear the canvas from any previous operations
              canvasContext.clearRect(0, 0, width, height);
              // Draw the render url to the canvas
              canvasContext.drawImage(documentImage, 0, 0, width, height);
              // Since the scene is already applying the trim, remove it from the surface. It is desirable to
              // have the scene do the trimming so that the rendering result we get doesn't lose more resolution
              // by cropping it internally. It also improves canvas rendering speed.
              return {
                surface: Object.assign(Object.assign({}, surface), {
                  trim: undefined
                }),
                canvas
              };
            }();
          }));
          return Promise.all(promises);
        }();
      });
    },
    dispose() {
      releaseCanvases(Object.values(documentCanvases));
      documentCanvases = {};
    }
  };
};
export const loadPreviewWithInstructions = _a => __awaiter(void 0, [_a], void 0, function (_ref6) {
  let {
    preview,
    tenant,
    category,
    renderingInstructionsUrl,
    vortexProduct,
    productLoadingOptions
  } = _ref6;
  return function* () {
    const adapter = createInstructionsAdapter({
      tenant,
      category
    });
    yield preview.load(vortexProduct, surfaces => adapter.generateDocumentCanvases({
      surfaces,
      renderingInstructionsUrl
    }), productLoadingOptions);
    adapter.dispose();
  }();
});
export const updatePreviewWithInstructions = _b => __awaiter(void 0, [_b], void 0, function (_ref7) {
  let {
    preview,
    tenant,
    category,
    renderingInstructionsUrl
  } = _ref7;
  return function* () {
    const adapter = createInstructionsAdapter({
      tenant,
      category
    });
    yield preview.update(surfaces => adapter.generateDocumentCanvases({
      surfaces,
      renderingInstructionsUrl
    }));
    adapter.dispose();
  }();
});